import { FawryInitRequestData } from "@core/checkout/types";
import { loadScript, loadStylesheet } from "@core/shared/utils";
import { errorToast } from "@design-system/components/toast";
import { useAction } from "@framework/shared/hooks";
import { sha1 } from "@mongez/encryption";
import { trans } from "@mongez/localization";
import { round } from "@mongez/reinforcements";
import { useEffect, useRef, useState } from "react";
import { paymentAtom } from "../atoms";

export function useFawryPayment() {
  const checkoutData = paymentAtom.use("checkoutData");
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fawryPayRef = useRef<any>();
  const initializeCheckout = useAction("/checkout");

  const initializePayment = async () => {
    try {
      setIsLoading(true);

      const data = {
        lessons: paymentAtom.get("lessons")?.map((lesson) => lesson.id),
        paymentMethod: paymentAtom.get("paymentMethod"),
      };

      const response = await initializeCheckout(data);

      if (response.isOK) {
        const paymentData: FawryInitRequestData = response.data.paymentData;

        // Load CSS assets
        if (paymentData.assetsUrls?.css) {
          paymentData.assetsUrls.css.forEach((url) => {
            loadStylesheet(url, sha1(url));
          });
        }

        // Load JS assets
        if (paymentData.assetsUrls?.js) {
          for (let i = 0; i < paymentData.assetsUrls.js.length; i++) {
            const url = paymentData.assetsUrls.js[i];
            const isLastScript = i === paymentData.assetsUrls.js.length - 1;

            await new Promise<void>((resolve) => {
              loadScript(url, () => {
                if (isLastScript) {
                  const FawryPay = (window as any)["FawryPay"];
                  fawryPayRef.current = FawryPay;
                  setIsInitialized(true);
                }
                resolve();
              });
            });
          }
        }

        paymentAtom.change("checkoutData", paymentData);
      } else {
        errorToast(response.error || trans("checkout.initializationFailed"));
      }
    } catch (error) {
      console.error("Failed to initialize payment:", error);
      errorToast(trans("checkout.initializationFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    initializePayment();
  }, []);

  const handleCheckout = () => {
    if (!fawryPayRef.current || !checkoutData) {
      errorToast(trans("checkout.paymentNotReady"));
      return;
    }

    try {
      setSubmitted(true);

      const FawryPay = fawryPayRef.current;
      const { paymentFees, totalAmount, ...data } = checkoutData.checkout;

      FawryPay.checkout(data, {
        locale: checkoutData.locale,
        mode: checkoutData.displayMode,
      });
    } catch (error) {
      console.error("Checkout error:", error);
      errorToast(trans("checkout.checkoutFailed"));
      setSubmitted(false);
    }
  };

  const paymentFees = round(checkoutData?.checkout?.paymentFees || 0, 2);
  const totalAmount = round(checkoutData?.checkout?.totalAmount || 0, 2);

  return {
    isLoading,
    isInitialized,
    submitted,
    paymentFees,
    totalAmount,
    handleCheckout,
  };
}
